import { compact } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldValues } from 'react-hook-form';

import { useSelectInputContext } from '@/modules/entities/contexts/selectInput/selectInput.context';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import { SelectInputOption } from '../../baseInputs/inputTypes';

type ResponsiveInputOption = SelectInputOption<unknown> | { type: 'component' };

interface UseResponsiveSelectInputProps<FormShape extends FieldValues> {
  fieldName: FieldNameFromFormShape<FormShape>;
  fieldValue: unknown;
  selectValue: (value: unknown) => void;
  options: ResponsiveInputOption[];
  disableAutomaticNewOptionSelection?: boolean;
}

/**
 * @description Hook to respond to new options and automatically select them
 */
export function useResponsiveSelectInput<FormShape extends FieldValues>({
  isOpen,
  handleClose,
  autoSelectGracePeriodMs = 5000,
  ...props
}: UseResponsiveSelectInputProps<FormShape> & {
  isOpen: boolean;
  handleClose: () => void;
  autoSelectGracePeriodMs?: number;
}) {
  const { activeSelectInput } = useSelectInputContext();
  const {
    fieldName,
    fieldValue,
    selectValue,
    options,
    disableAutomaticNewOptionSelection = false,
  } = props;

  const isActiveSelectInput = activeSelectInput === fieldName;
  const deactivationTimeRef = useRef<number | null>(null);
  const wasActiveRef = useRef<boolean>(false);

  // Track when this input goes from active to inactive
  useEffect(() => {
    if (isActiveSelectInput) {
      // Input became active
      wasActiveRef.current = true;
      deactivationTimeRef.current = null; // Clear any previous deactivation time
    } else if (wasActiveRef.current) {
      // Input went from active to inactive - start grace period
      deactivationTimeRef.current = Date.now();
      wasActiveRef.current = false;
    }
  }, [isActiveSelectInput]);

  // Check if we're within the grace period
  const isWithinGracePeriod = useCallback(() => {
    if (isActiveSelectInput) return true;
    if (!deactivationTimeRef.current) return false;

    return Date.now() - deactivationTimeRef.current <= autoSelectGracePeriodMs;
  }, [isActiveSelectInput, autoSelectGracePeriodMs]);

  // Remove component options from options list
  const selectableOptions: SelectInputOption<unknown>[] = useMemo(() => {
    return compact(
      options.map((o) => {
        if (o.type === 'component') {
          return null;
        } else {
          return o;
        }
      })
    );
  }, [options]);

  const oldSelectableOptionsRef = useRef<typeof selectableOptions>([]);

  useEffect(
    // Wait for the value options to be loaded before initializing old options
    function initializeOldOptions() {
      if (selectableOptions.length && !oldSelectableOptionsRef.current.length) {
        oldSelectableOptionsRef.current = selectableOptions;
      }
    },
    [isOpen, selectableOptions]
  );

  useEffect(
    // Respond to changes in options and assume that if an option is added, it should be selected
    function selectAddedOption() {
      if (
        !selectableOptions.length ||
        !oldSelectableOptionsRef.current.length
      ) {
        return;
      }

      const oldOptionValues = new Set(
        oldSelectableOptionsRef.current.map((o) => o.value)
      );

      const newSelectableOption = selectableOptions.find((o) => {
        return !oldOptionValues.has(o.value);
      });

      const gracePeriodResult = isWithinGracePeriod();

      if (
        !fieldValue &&
        newSelectableOption &&
        gracePeriodResult &&
        !disableAutomaticNewOptionSelection
      ) {
        // If new option is found, select it
        selectValue(newSelectableOption.value);
        // Close the select input
        handleClose();
      }

      oldSelectableOptionsRef.current = selectableOptions;
    },
    [
      disableAutomaticNewOptionSelection,
      fieldName,
      fieldValue,
      handleClose,
      isActiveSelectInput,
      isWithinGracePeriod,
      selectValue,
      selectableOptions,
    ]
  );
}
